import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { OnExchangeEligibilityStateStructure, ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME } from 'src/app/angular-elements/on-exchange-enrollment/reducers/eligibility.reducer';
import { DateService } from 'src/app/shared/services/date.service';
//import * as moment from 'moment';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { EligibilityManagerService } from 'src/app/angular-elements/on-exchange-enrollment/services/eligibility-manager.service';
import { PageIndividualOnExchangePlanEnrollmentService } from '../../services/page-individual-on-exchange-plan-enrollment.service';
import { ConsumerNpnControlsAsJson } from 'src/app/angular-elements/shared/interfaces/on-exchange-eligibility-form.interface';

interface ConsumerNpnControls {
  workingWithProducerIndicator: AbstractControl;
  producerFirstName: AbstractControl;
  producerLastName: AbstractControl;
  npn: AbstractControl;
  verifyNpn: AbstractControl;
}

/**
 * CONSUMER FLOW
 * 1) If you're working with a producer, it will collect the NPN number
 * 2) If you're working alone, it will that's fine
 */
@FormComponentLookup('marketplace-consumer-npn')
@Component({
    selector: 'app-marketplace-consumer-npn',
    templateUrl: './marketplace-consumer-npn.component.html',
    styleUrls: ['./marketplace-consumer-npn.component.scss'],
    standalone: false
})
export class MarketplaceConsumerNpnComponent extends FormEntryComponentClass implements OnInit, OnDestroy {

  consumerNpnForm: UntypedFormGroup;
  consumerNpnFormChangesSubscription: Subscription;
  workingWithProducerIndicatorFormChangesSubscription: Subscription;

  nameNodeValue: string;
  signOrSubmissionDate: string;

  constructor(
    private fb: UntypedFormBuilder,
    private dateService: DateService,
    private eligibilityManagerService: EligibilityManagerService,
    private pageIndividualOnExchangePlanEnrollmentService: PageIndividualOnExchangePlanEnrollmentService
  ) {
    super();
  }

  get consumerNpnFormControls(): ConsumerNpnControls {
    return this.consumerNpnForm.controls as unknown as ConsumerNpnControls;
  }

  get consumerNpnFormControlsAsJson(): ConsumerNpnControlsAsJson {
    return this.consumerNpnForm.value as unknown as ConsumerNpnControlsAsJson;
  }

  ngOnDestroy() {
    this.consumerNpnFormChangesSubscription?.unsubscribe();
    this.workingWithProducerIndicatorFormChangesSubscription?.unsubscribe();
  }

  ngOnInit(): void {

    this.signOrSubmissionDate = moment(this.dateService.getNow()).format('MM/DD/YYYY');

    const eligibilityState: OnExchangeEligibilityStateStructure
      = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    const matchedProspects = eligibilityState.prospects.filter(p => eligibilityState.currentProspectId === p.prospectId );

    if (matchedProspects.length > 0) {
      /** Create Form */
      this.consumerNpnForm = this.fb.group({});
      /** Watch Form */
      this.setupSubscription();
      /** Populate Form */
      this.setupForm();
    }

  }

  setupControlChangeSubscription() {

    this.workingWithProducerIndicatorFormChangesSubscription
      = this.consumerNpnFormControls.workingWithProducerIndicator
        .valueChanges
        .subscribe( (v: 'true' | 'false') => {

          /** When we say "yes" to the question, re-populate the values from the eligibility data */
          if (v === 'false') {

            this.consumerNpnFormControls.producerFirstName.clearValidators();
            this.consumerNpnFormControls.producerLastName.clearValidators();
            this.consumerNpnFormControls.npn.clearValidators();
            this.consumerNpnFormControls.verifyNpn.clearValidators();

            this.consumerNpnFormControls.producerFirstName.reset();
            this.consumerNpnFormControls.producerLastName.reset();
            this.consumerNpnFormControls.npn.reset();
            this.consumerNpnFormControls.verifyNpn.reset();

          } else {

            const producerInfoFromEligibilityData = this.eligibilityManagerService.getAgentInformation();

            const fName = producerInfoFromEligibilityData.fullName.firstName;
            const lName = producerInfoFromEligibilityData.fullName.lastName;
            const npn = producerInfoFromEligibilityData.npn;

            if (fName && lName && npn) {
              this.consumerNpnFormControls.producerFirstName.setValue(fName);
              this.consumerNpnFormControls.producerLastName.setValue(lName);
              this.consumerNpnFormControls.npn.setValue(npn);
              this.consumerNpnFormControls.verifyNpn.setValue(npn);
            }

            this.consumerNpnFormControls.producerFirstName.setValidators([Validators.required]);
            this.consumerNpnFormControls.producerLastName.setValidators([Validators.required]);
            this.consumerNpnFormControls.npn.setValidators([Validators.required]);
            this.consumerNpnFormControls.verifyNpn.setValidators([Validators.required, this.npnVerificationValidator()]);

          }

          this.consumerNpnForm.updateValueAndValidity();
          this.pageIndividualOnExchangePlanEnrollmentService.reportValidity(this.consumerNpnForm, this.entry);
        });
  }

  setupSubscription() {

    this.consumerNpnFormChangesSubscription = this.consumerNpnForm.valueChanges
    .pipe(debounceTime(300))
    .subscribe((v: ConsumerNpnControlsAsJson) => {

      const dynamicObject = {};
      dynamicObject[this.entry.formControlAttributes.formControlName] = v;


      /** Update global form (trigger save to state) */
      this.form.patchValue(dynamicObject);
      this.pageIndividualOnExchangePlanEnrollmentService.reportValidity(this.consumerNpnForm, this.entry);

    });

  }

  setupForm() {

    const producerInfoFromEligibilityData = this.eligibilityManagerService.getAgentInformation();

    const fName = producerInfoFromEligibilityData.fullName.firstName;
    const lName = producerInfoFromEligibilityData.fullName.lastName;
    const npn = producerInfoFromEligibilityData.npn;

    /** If producer info is available, use that for form values */
    if ( fName && lName && npn ) {
      this.consumerNpnForm.addControl(
        'workingWithProducerIndicator',
        this.fb.control(true, [Validators.required])
      );
      this.consumerNpnForm.addControl(
        'producerFirstName',
        this.fb.control(fName, [Validators.required])
      );
      this.consumerNpnForm.addControl(
        'producerLastName',
        this.fb.control(lName, [Validators.required])
      );
      this.consumerNpnForm.addControl(
        'npn',
        this.fb.control(npn, [Validators.required])
      );
      this.consumerNpnForm.addControl(
        'verifyNpn',
        this.fb.control(npn, [Validators.required])
      );

      /** Disable all the controls */
      Object.keys(this.consumerNpnForm.controls).forEach( cName => {
        this.consumerNpnForm.controls[cName].disable();
      });

    } else {
      this.consumerNpnForm.addControl(
        'workingWithProducerIndicator',
        this.fb.control(false, [Validators.required])
      );
      this.consumerNpnForm.addControl(
        'producerFirstName',
        this.fb.control(null, [Validators.required])
      );
      this.consumerNpnForm.addControl(
        'producerLastName',
        this.fb.control(null, [Validators.required])
      );
      this.consumerNpnForm.addControl(
        'npn',
        this.fb.control(null, [Validators.required])
      );
      this.consumerNpnForm.addControl(
        'verifyNpn',
        this.fb.control(null, [Validators.required, this.npnVerificationValidator()])
      );
    }

    this.consumerNpnForm.updateValueAndValidity();
    this.pageIndividualOnExchangePlanEnrollmentService.reportValidity(this.consumerNpnForm, this.entry);
    this.setupControlChangeSubscription();
  }


  npnVerificationValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (
        this.consumerNpnFormControlsAsJson.npn &&
        this.consumerNpnFormControlsAsJson.npn !== control.value
      ) {
        return { npnVerificationFailed: true };
      }
      return null;
    }
  }
}
